import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {LecturerNews} from '../../../redux/lecturer/lecturerSlice'

const LecturerDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [news, setNews] = useState<any[]>([])
  const [expandedNews, setExpandedNews] = useState<{[key: string]: boolean}>({})

  useEffect(() => {
    dispatch(LecturerNews()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setNews(res.payload)
        // Initialize all news items as not expanded
        const initialExpandedState = res.payload.reduce((acc: any, item: any) => {
          acc[item.id] = false
          return acc
        }, {})
        setExpandedNews(initialExpandedState)
      }
      setLoader(false)
    })
  }, [])

  // Function to format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }
    return new Date(dateString).toLocaleDateString('en-GB', options).replace(',', '')
  }

  // Function to strip HTML tags and optionally limit content
  const processContent = (content: string | undefined, isExpanded: boolean) => {
    if (!content) return ''

    if (isExpanded) return content // full content with HTML

    const maxLength = 300

    const div = document.createElement('div')
    div.innerHTML = content

    let currentLength = 0
    let result = ''

    const walkNodes = (nodes: any): boolean => {
      for (let node of nodes) {
        if (currentLength >= maxLength) return false

        if (node.nodeType === Node.TEXT_NODE) {
          const remaining = maxLength - currentLength
          const text = node.nodeValue?.substring(0, remaining) || ''
          result += text
          currentLength += text.length
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase()
          // Keep only these tags
          if (['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'li'].includes(tagName)) {
            result += `<${tagName}>`
            const keepWalking = walkNodes(node.childNodes)
            result += `</${tagName}>`
            if (!keepWalking) return false
          } else {
            // Skip unknown tags but keep children
            const keepWalking = walkNodes(node.childNodes)
            if (!keepWalking) return false
          }
        }
      }
      return true
    }

    walkNodes(div.childNodes)

    return result + '...'
  }

  const toggleExpand = (id: string) => {
    setExpandedNews((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  if (loader) {
    return <Loader />
  }

  return (
    <div className='container-fluid p-0'>
      <div className='card mb-5 mb-xl-10 mt-0 shadow-sm rounded-4 border-0'>
        <div className='card-header border-0 bg-white py-4 rounded-top-4'>
          <h3 className='fw-bold text-primary m-0'>{t('LABEL.NEWS_AND_ANNOUNCEMENT')}</h3>
        </div>

        {/* No Data */}
        {news.length < 1 && (
          <div className='card-body'>
            <h4 className='text-center text-danger fw-bold py-5 m-0'>
              {t('GLOBAL.DATA_NOT_AVAILABLE')}
            </h4>
          </div>
        )}

        {/* News List */}
        {news.length > 0 && (
          <div className='card-body py-4'>
            <div className='timeline'>
              {news.map((item: any) => (
                <div key={item.id} className='timeline-item mb-5 position-relative ps-5'>
                  {/* Timeline Dot */}
                  <span
                    className='position-absolute top-0 start-0 translate-middle bg-primary rounded-circle'
                    style={{width: '14px', height: '14px'}}
                  ></span>

                  {/* News Card */}
                  <div className='p-4 bg-light rounded-4 shadow-sm w-100'>
                    {/* Title */}
                    <h4 className='fw-bold text-primary mb-1'>{item.title}</h4>

                    {/* Date */}
                    <div className='text-muted small mb-3 fs-5'>
                      <i className='fas fa-calendar-alt mx-1'></i>
                      {formatDate(item?.created_at)}
                    </div>

                    {/* Content */}
                    <p
                      className='card-text mb-3'
                      dangerouslySetInnerHTML={{
                        __html: processContent(item?.content, expandedNews[item.id]),
                      }}
                    ></p>

                    {/* Read More / Show Less */}
                    {item?.content?.length > 300 && (
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className='btn btn-sm btn-outline-primary rounded-pill px-3 d-flex align-items-center'
                      >
                        {expandedNews[item.id] ? t('LABEL.SHOW_LESS') : t('LABEL.READ_MORE')}
                        <i
                          className={`fas fa-chevron-${
                            expandedNews[item.id] ? 'up' : 'right'
                          } ms-2`}
                        ></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LecturerDashboard
